import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Landmark, Umbrella, ClipboardCheck, ArrowUpRight, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { Cm3Badge } from "../components/Cm3Badge";
import AssessmentForm from "../components/AssessmentForm";
import { CM3, CM3_MEANING, CM3_VERIFY_STEPS, CM3_FAQS } from "../data/compliance";
import { DOMAIN } from "../data/contact";

const ICONS = { ShieldCheck, Landmark, Umbrella, ClipboardCheck };

const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d) ? iso : d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
};

function Facts({ cm3 }) {
  const rows = [
    ["Cm3 ID", cm3.id],
    ["Capability", `${CM3.capability} — ${CM3.scope.join(", ")}`],
    ["Public Liability", cm3.publicLiability],
    ["Workers Compensation", CM3.workersComp],
    ["Current risk review valid to", fmtDate(cm3.expiry)],
  ];
  return (
    <dl className="speakable grid gap-px overflow-hidden rounded-lg border border-white/10 bg-white/10 sm:grid-cols-2" data-testid="cm3-facts">
      {rows.map(([k, v]) => (
        <div key={k} className="bg-obsidian-800 p-5">
          <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{k}</dt>
          <dd className="mt-1.5 font-heading font-bold text-white">{v}</dd>
        </div>
      ))}
      <div className="bg-obsidian-800 p-5 sm:col-span-2">
        <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Verify</dt>
        <dd className="mt-1.5">
          <a href={cm3.verifyUrl} target="_blank" rel="noreferrer" data-testid="cm3-verify-link"
             className="inline-flex items-center gap-1.5 font-heading font-bold text-gold hover:text-gold-hover">
            Check our live status on the Cm3 portal <ArrowUpRight size={16} />
          </a>
        </dd>
      </div>
    </dl>
  );
}

export default function Compliance() {
  const [cm3, setCm3] = useState({ id: CM3.id, expiry: CM3.expiry, verifyUrl: CM3.verifyUrl, publicLiability: CM3.publicLiability });
  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setCm3({ id: s.cm3_id || CM3.id, expiry: s.cm3_expiry || CM3.expiry, verifyUrl: s.cm3_verify_url || CM3.verifyUrl, publicLiability: s.public_liability || CM3.publicLiability });
    }).catch(() => {});
  }, []);
  const [open, setOpen] = useState(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${DOMAIN}/#organization`,
        name: "Speedy Resurfacing",
        url: DOMAIN,
        logo: `${DOMAIN}/brand/speedy-resurfacing-logo.png`,
        telephone: "0466 495 495",
        email: "info@speedyresurfacing.com.au",
        hasCredential: {
          "@type": "EducationalOccupationalCredential",
          name: "Cm3 Contractor WHS Prequalification",
          credentialCategory: "Prequalification",
          identifier: cm3.id,
          recognizedBy: { "@type": "Organization", name: "Cm3", url: cm3.verifyUrl },
          validFor: `Risk review valid to ${fmtDate(cm3.expiry)}`,
          about: `${CM3.capability}: ${CM3.scope.join(", ")}`,
        },
      },
      {
        "@type": "WebPage",
        "@id": `${DOMAIN}/compliance`,
        url: `${DOMAIN}/compliance`,
        name: "Cm3 Prequalified Court Resurfacing Contractor — Sydney",
        speakable: { "@type": "SpeakableSpecification", cssSelector: [".speakable"] },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN },
            { "@type": "ListItem", position: 2, name: "Compliance", item: `${DOMAIN}/compliance` },
          ],
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: CM3_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <div className="min-h-screen noise" data-testid="compliance-page">
      <Seo
        title="Cm3 Prequalified Court Resurfacing Contractor Sydney | Speedy Resurfacing"
        description={`Speedy Resurfacing is Cm3 prequalified (Cm3 ID ${cm3.id}) — independently verified WHS systems, ${cm3.publicLiability} public liability and NSW workers compensation. Procurement-ready court resurfacing for schools, councils, clubs and strata across Sydney & NSW.`}
        path="/compliance"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <nav className="flex items-center gap-2 font-mono text-xs text-zinc-500" data-testid="compliance-breadcrumb">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span><span className="text-zinc-300">Compliance</span>
          </nav>
          <div className="mt-8 grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-end">
            <Reveal>
              <Overline className="text-gold">Compliance &amp; Safety</Overline>
              <h1 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tighter">
                CM3 PREQUALIFIED.<br /><span className="gold-text">PROCUREMENT-READY.</span>
              </h1>
              <p className="speakable mt-6 max-w-2xl text-lg text-zinc-300" data-testid="compliance-summary">
                Speedy Resurfacing is a Cm3 prequalified court resurfacing contractor — Cm3 ID {cm3.id}. Our WHS system,
                risk review and insurances are independently verified, so schools, councils, clubs and strata across Sydney
                and NSW can engage us with confidence and without a separate vetting round.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Cm3Badge id={cm3.id} size="lg" to="#facts" />
                <a href={cm3.verifyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-gold">
                  Verify on Cm3 <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div id="facts" className="scroll-mt-32">
                <Facts cm3={cm3} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24" data-testid="compliance-meaning">
        <Reveal>
          <Overline>What it means for you</Overline>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight">
            SAFETY YOU CAN <span className="text-neon">VERIFY, NOT JUST TRUST.</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {CM3_MEANING.map((m, i) => {
            const Icon = ICONS[m.icon];
            return (
              <Reveal key={m.title} delay={(i % 2) * 0.06}>
                <div className="group h-full rounded-lg border border-white/10 bg-obsidian-800 p-7 hover:border-gold/50 transition-colors" data-testid={`cm3-meaning-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-gold/10 text-gold group-hover:bg-gold group-hover:text-obsidian transition-colors"><Icon size={24} /></span>
                  <h3 className="mt-6 font-heading font-bold text-xl">{m.title}</h3>
                  <p className="mt-2 text-sm md:text-base text-zinc-300">{m.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/10 bg-obsidian-800/40">
        <div className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Overline className="text-gold">For procurement teams</Overline>
            <h2 className="mt-4 font-heading font-black text-3xl md:text-4xl tracking-tight">HOW TO VERIFY US ON CM3.</h2>
            <ol className="mt-8 space-y-4" data-testid="cm3-verify-steps">
              {CM3_VERIFY_STEPS.map((s, i) => (
                <li key={s.title} className="flex items-start gap-5 rounded-lg border border-white/10 bg-obsidian-800 p-6">
                  <span className="font-heading font-black text-3xl text-gold/60">0{i + 1}</span>
                  <div>
                    <h3 className="font-heading font-bold text-lg">{s.title}</h3>
                    <p className="mt-1 text-sm text-zinc-300">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>Frequently asked</Overline>
            <h2 className="mt-4 font-heading font-black text-3xl md:text-4xl tracking-tight">CM3, INSURANCE &amp; WHS.</h2>
            <div className="mt-8 divide-y divide-white/10 rounded-lg border border-white/10 bg-obsidian-800" data-testid="cm3-faq">
              {CM3_FAQS.map((f, i) => (
                <div key={f.q}>
                  <button type="button" onClick={() => setOpen(open === i ? -1 : i)} data-testid={`cm3-faq-${i}`}
                          className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-heading font-bold hover:text-gold transition-colors">
                    {f.q}
                    <ArrowRight size={16} className={`shrink-0 transition-transform ${open === i ? "rotate-90 text-gold" : ""}`} />
                  </button>
                  {open === i && <p className="px-6 pb-6 text-sm md:text-base text-zinc-300">{f.a}</p>}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 py-16 md:py-24" id="enquiry">
        <Reveal>
          <AssessmentForm testid="compliance-assessment-form" />
        </Reveal>
      </section>
    </div>
  );
}
